import { AggregatedLineItemLevelTaxResponse } from './edit-invoice/invoicesEditSlice';
import { AttachmentFile, FileTypes, ForwardFileType, GenericPaginationResponse } from 'types/baseTypes';
import { AbstractITax } from 'containers/product-catalog/taxes/taxes.model';
import { InvoiceSourceEnum, InvoiceVersion, TipTimingEnum, TransactionType } from './constants';
import { FeatureToggleState } from 'containers/settings/constants';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { PaymentMode, TNC } from 'containers/settings/settings.model';
import { RelatedEstimate } from 'containers/jobs/jobs.model';
import { ReminderCondition } from 'containers/settings/estimate-settings/estimateSetting.model';

export interface GetCustomersPayload {
  teamId: number;
}

export interface GetMerchantsPayload {
  teamId: number;
}

export interface GetCustomersResponse {
  customers: CustomerItem[];
  totalCount: number;
}

export interface CustomerItem {
  id?: string;
  ID: string;
  name: string;
  phoneNumber: string;
  email: string;
  merchantId: string;
  billingAddress: AddressField;
  lastName: string;
}

export interface Merchant {
  userId: number;
  id: number;
  title: string;
  completed: boolean;
}

export enum ShareReceiptPageState {
  MAIN = 'Main',
  SHARE_QR = 'shareQR'
}

export interface GetInvoicesResponse {
  invoices: IInvoice[];
  totalCount: number;
}

export interface ImageUploadResponse {
  imageUrl: string;
}

export interface PresignedUrlAttachment {
  filename: string;
  fileType: string;
}

export interface PresignedUrlRequest {
  case: string;
  attachments: PresignedUrlAttachment[];
}

export interface PresignedUrlResponseItem {
  fileName: string;
  updatedFileName: string;
  fileType: string;
  presignedURL: string;
  path: string;
  getPresignedURL?: string;
}

export interface PresignedUrlResponse {
  data: PresignedUrlResponseItem[];
}

export interface GetPresignedUrlsPayload {
  teamId: string;
  case: string;
  attachments: PresignedUrlAttachment[];
}

export interface CloseInvoicePayload {
  teamId: string;
  invoiceId: string;
  closureReason: string;
  closureAmount: number;
  closureNote: string;
}

export interface CloseInvoiceResponse {
  success: boolean;
  message: string;
}

export interface IInvoice {
  // TODO additionalTax: remove related fields by replacing it with service charge
  invoiceId: string;
  invoiceNumber: string;
  createdAt: string;
  status: InvoiceStatus;
  isPaid: boolean;
  amount: string;
  surchargePercentage: string;
  additionalTaxPercentage: string;
  billAmount: string;
  surchargeAmount: string;
  additionalTaxAmount: string;
  tipAmount: string;
  payableAmount: string;
  paymentMethod: string;
  merchantId: string;
  merchantProfilePicureUrl: string;
  merchantName: string;
  merchantBrandName: string;
  merchantBusinessName: string;
  merchantPhone: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  lastTrnxDate: string;
  isModifiable: boolean;
}

export interface Member {
  id: string;
  name: string;
  profilePicture: string;
  noOfTxns: number;
  totalAmount: string;
  tip: string;
}

export interface InvoiceByIdPayload {
  teamId: number;
  invoiceId: string;
  isFromEdit?: boolean;
  includeTransactions?: boolean;
  isFromJob?: boolean;
  isFromEstimate?: boolean;
  timezone?: string;
}

export interface TaxesAndFeesPayload {
  teamId: number;
}

export interface IServiceChargeTaxFromAPIResponse extends AbstractITax {
  id: number; // tax maintained in the invoice table
  catalogueTaxId: number; // the particular tax's id in the catalog
  name: string;
  percentage: string;
  amount?: number;
}

export interface LineItemTaxFromAPIResponse {
  id: number; // tax maintained in the invoice table
  catalogueTaxId: number; // the particular tax's id in the catalog
  name: string;
  percentage: string;
  amount?: number;
}

export interface LineItem {
  catalogueLineItemId: number;
  id: number; // id -> index of a line item that we use in the front-end
  name: string;
  qty: string;
  amount: string;
  unitPrice: string;
  additionalDetails: string;
  taxes?: LineItemTaxFromAPIResponse[];
  sku?: string;
  unitCost?: string;
  markupPercentage?: string;
}

export interface IInvoiceServiceCharge {
  id: number;
  name: string;
  amount: string;
  percentage: string;
  taxes: AbstractITax[];
  totalAmount?: string;
}

export interface IInvoiceServiceChargeFromResponse {
  id: number;
  name: string;
  amount: string;
  percentage: string;
  taxes?: IServiceChargeTaxFromAPIResponse[];
  totalAmount?: string;
}

export interface InvoiceByIdResponse {
  invoiceId: string;
  invoiceNumber: string;
  invoiceStatus: InvoiceStatus;
  comment: string;
  customer: Customer;
  createdAt: string;
  createdTime: string;
  paidDate: string;
  paidTime: string;
  currencySymbol: string;
  transactionId: string;
  transactionStatus: string;
  paymentMethod: string;
  amount: string;
  tipAmount: string;
  billAmount: string;
  surchargeAmount: string;
  errorCode: string;
  storeName: string;
  merchantName: string;
  merchantTaxId: string;
  merchantAddress: string;
  merchantPhoneNumber: string;
  isPaid: boolean;
  url: string;
  qr: string;
  hasSurcharge: boolean;
  hasTip: boolean;
  invoiceMessage: string;
  payoutStatus: string;
  payoutCreatedBy: string;
  hasAdditionalTax: boolean; // unused?
  lineItems: LineItem[];
  teamIcon: string;
  surcharge: Surcharge;
  merchantEmail: string;
  merchantWebsite: string;
  tipPercentage: string;
  payableAmount: string;
  discount: string;
  discountPercentage: string;
  attachments: Attachment[];
  merchantProfilePicture: string;
  isEditable: boolean;
  merchantId: string;
  hasDiscount: boolean;
  dueDate: string;
  isManualPayment: boolean;
  createdBy: string;
  receiptQr: string;
  version: string;
  qrId: string;
  transactionDetails: TransactionDetails;
  isModifiable: boolean;
  tnc: TNC;
  taxes: AggregatedLineItemLevelTaxResponse[];
  serviceCharges: IInvoiceServiceChargeFromResponse[];
  isDiscountAmountSelected: boolean;
  isInvoiceSigned: boolean;
  isSignatureEnabled: boolean;
  signatureCustomerName: string;
  signedAt: string;
  signaturePresignedUrl: string;
  isServiceFeeEnabled: boolean;
  timeline: InvoiceTimelineEvent[];
  timelineStatus: InvoiceStatus;
  receiptUrl: string;
  jobData: JobData[];
  paymentModes: PaymentMode[];
  estimates: RelatedEstimate[]; // New property
  partialPaymentEnabled?: boolean;
  paymentRemindersEnabled?: boolean;
  paymentReminders?: InvoiceReminder[];
  tipTiming?: TipTimingEnum;
}

export interface JobData {
  jobId: string;
  jobNumber: string;
  jobStatus: string;
  createdBy: string;
  profilePicture: string;
  createdAt: string;
  scheduledFrom: string;
  scheduledTo: string;
  customer: CustomerForJobFromInvoice;
  jobTitle: string;
  payableAmount: string;
  merchantName: string;
  hasPermission: boolean;
  isEditable: boolean;
}

export interface CustomerForJobFromInvoice {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: AddressField;
}

export interface Customer {
  id: string;
  ID: string;
  name: string;
  phoneNumber: string;
  email: string;
  merchantId: string;
  billingAddress: AddressField;
  lastName: string;
  serviceAddress?: AddressField[];
  companyName?: string;
}
export interface InvoiceCustomerV2 {
  id: string;
  ID?: string;
  name: string;
  phoneNumber: string;
  email: string;
  billingAddress: AddressField;
  lastName: string;
  serviceAddress?: AddressField;
  companyName?: string;
}

export interface AdditionalTax {
  id: number;
  name: string;
  percentage: string;
}

export interface Surcharge {
  enabled: boolean;
  percentage: string;
}

export interface TransactionDetails {
  transactions: Transaction[];
  totalCount: number;
}

export interface Transaction {
  // TODO additionalTax: remove related fields by replacing it with service charge
  id: string;
  status: string;
  amount: string;
  tipAmount: string;
  merchantId: string;
  teamId: number;
  sourceType: string;
  paymentMethod: string;
  reason: string;
  createdAt: string;
  profilePicture: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  merchantRoleFirstName: string;
  merchantRoleLastName: string;
  merchantRolePhone: string;
  merchantBrandName: string;
  merchantName: string;
  merchantBusinessName: string;
  merchantPhone: string;
  parentID: string;
  surchargeAmount: string;
  additionalTaxAmount: string;
  billAmount: string;
  refundTransactionId: string;
  refundTransactions: RefundTransaction[];
  disputeTransactions: DisputeTransaction[];
}

export interface RefundTransaction {
  ID: string;
  paymentMethod: string;
  amount: string;
  status: string;
  createdAt: string;
  reason: string;
  source: string;
  customerName: string;
  profilePicture: string;
  merchantName: string;
  merchantId: string;
  parentID: string;
  refundTransactions: RefundTransaction[];
  disputeTransactions: DisputeTransaction[];
}

export interface DisputeTransaction {
  disputeId: string;
  disputeTransactionId: string;
  disputeAmount: string;
  createdAt: string;
  status: string;
  hasPermission: boolean;
}

export interface CustomerByIdPayload {
  customerId: string;
  teamId: number | string;
}

interface InvoiceMetadata {
  amount: string;
  count: number;
}

export interface AddressField {
  id: number;
  street: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  addressNote: string;
  addressName: string;
  line2?: string;
  type?: ADDRESS_TYPE;
  latitude?: number;
  longitude?: number;
}

export interface CustomerDetails {
  id: string;
  merchantId: string;
  teamID: number;
  name: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  billingAddressId: number;
  customerNumber: string;
  companyName: string;
  referenceNumber: string;
  note: string;
  isBillingSameAsService: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  paidInvoice: InvoiceMetadata;
  pendingInvoice: InvoiceMetadata;
  serviceAddress: AddressField[];
  billingAddress: AddressField;
}

export interface DiscountSettings {
  enabled: boolean;
}

export interface TaxesAndFeesResponse {
  surcharge: Surcharge;
  tip: Tip;
  discount: DiscountSettings;
  tnc: TNC;
}

export interface AdditionalTax {
  id: number;
  name: string;
  percentage: string;
}

export interface Surcharge {
  enabled: boolean;
  percentage: string;
  basePoints: string;
  config: FeatureToggleState;
}

export interface Tip {
  enabled: boolean;
  percentage: string;
}

export interface InvoiceSummary {
  firstPaymentDate: string;
  lastPaymetDate: string;
  amountCollected: number;
  pendingAmount: number;
  paidInvoicesCount: number;
  pendingInvoicesCount: number;
}

export interface Attachment {
  urlLink: string;
  url: string;
  title: string;
  description: string;
  source?: string;
  id?: string;
  sourceAttachmentId?: number;
}

export enum InvoiceStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  PAID_QR = 'PAID-QR',
  REFUNDED = 'REFUNDED',
  PROCESSING = 'PROCESSING',
  REFUND_INITIATED = 'REFUND_INITIATED',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  OVERDUE = 'OVERDUE',
  CLOSED_AMOUNT_ADJUSTED = 'CLOSED_AS_AMOUNT_ADJUSTED',
  CLOSED_BAD_DEBT = 'CLOSED_AS_BAD_DEBT'
}

export interface StatusDisplayData {
  primaryAmount: {
    label: string;
    value: number;
    tipAmount?: number;
  };
  breakdown?: Array<{
    label: string;
    value: number;
    tipAmount?: number;
  }>;
  statusBadge: {
    text: string;
    backgroundColor: string;
    textColor: string;
  };
}

export interface EssentialInvoiceData {
  status: string;
  totalAmount: number;
  pendingAmount: number;
  totalPaidAmount: number;
  totalPaidAfterRefund: number;
  totalRefundedAmount: number;
  statusIndicator: string;
  tipAmount: number;
  totalInvoicedAmount: number;
  closureAmount: number;
}

export interface ILineItemTaxRequest {
  catalogueTaxId: number;
  name: string;
  percentage: string;
  amount: string;
}

export interface ILineItemRequest {
  catalogueLineItemId?: number;
  name: string;
  qty: string;
  amount: string;
  unitPrice: string;
  unitCost?: string;
  markupPercentage?: string;
  additionalDetails: string;
  taxes?: ILineItemTaxRequest[];
  sku?: string;
}
export interface IInvoiceServiceChargeTaxRequest {
  catalogueTaxId: number;
  name: string;
  percentage: string;
  amount: string;
}

export interface IInvoiceServiceChargeRequest {
  name: string;
  amount: string;
  percentage: string;
  totalAmount: string; // Total service charge excluding the taxes applied on it - base amount
  taxes?: IInvoiceServiceChargeTaxRequest[];
}
export interface IInvoiceAttachmentRequest {
  url: string;
  title: string;
  description?: string;
  source?: string;
}

export interface IUniqueTaxRequest {
  name: string;
  percentage: string;
  amount: string;
}

export interface InvoicePayload {
  version: InvoiceVersion;
  teamId: string;
  storeName: string;
  amount: string; // subtotal
  comment: string; // notes
  customerId?: string;
  phoneNumber?: string;
  email?: string;
  customerName?: string;
  billingAddress?: AddressField;
  lineItems: ILineItemRequest[];
  discount?: string;
  discountPercentage?: string;
  isDiscountAmountSelected?: boolean;
  surchargePercentage: string;
  attachments?: IInvoiceAttachmentRequest[];
  tipEnabled: boolean;
  tncId: number;
  taxes?: IUniqueTaxRequest[]; // A Tax could be applied on one or more line item and/or on the service charge
  // all the items in this list will be unique
  invoiceNumber?: string;
  serviceCharges: IInvoiceServiceChargeRequest[];
  serviceChargeAmount: string;
  source: InvoiceSourceEnum;
  isSignatureEnabled: boolean;
  isServiceFeeEnabled: boolean;
  serviceAddressId: number;

  // New property for relative entity
  relativeEntity?: {
    entityType: 'ESTIMATE'; // Assuming this will always be ESTIMATE
    entityId: string; // The ID of the estimate
    entityNumber: string; // The number of the estimate
  };

  // Ensure paymentModes remains intact
  paymentModes: PaymentModeInvoicePayload[];
  surchargeAmount: string;
}

export interface InvoiceReminder {
  id?: string;
  name: string;
  condition: ReminderCondition;
  remindOn: string;
  isSent: boolean;
}

export interface EditInvoicePayload {
  invoiceId: string;
  invoicePayload: InvoicePayload;
}

export interface EditInvoicePayloadV2 {
  invoiceId: string;
  invoicePayload: InvoicePayloadV2;
}

export interface PaymentModeInvoicePayload {
  id: number;
  enabled: boolean;
}
export interface InvoiceResponse {
  qrId: string;
  qr: string;
  url: string;
  invoiceId: string;
  customerId: string;
  isNewCustomer: boolean;
  invoiceMessage: string;
  customer: Customer;
  billAmount: string;
  payableAmount: string;
  checkoutURL: string;
}

export interface InvoiceResponseV2 {
  qrId: string;
  invoiceQR: string;
  checkoutURL: string;
  invoiceId: string;
  customerId: string;
  isNewCustomer: boolean;
  invoiceMessage: string;
  customer: Customer;
  invoiceCustomer: InvoiceCustomerV2;
  subTotalAmount: string; // billable amount- amount before taxes and service fees
  totalAmount: string; // payable amount-final amount
  isDiscountAmountSelected: boolean;
  referenceId: string;
  invoiceNumber: string;
}

/** API returns Entity as "LINE_ITEM" | "CUSTOMER" (uppercase with underscore). Use lowercase for comparison. */
export const RISK_VALIDATION_ENTITY = {
  LINE_ITEM: 'line_item',
  CUSTOMER: 'customer'
} as const;

export interface RiskValidationErrorResponse {
  entity: string;
  message: string;
}

export interface InvoiceRiskValidationResponse {
  errors: RiskValidationErrorResponse[];
  warnings: string[];
}

export interface CreateInvoiceWithRiskValidationRequest extends InvoicePayloadV2 {
  skipWarnings?: boolean;
}

export interface CreateInvoiceWithRiskValidationResponse extends InvoiceResponseV2 {
  isRiskValidationFailed: boolean;
  riskValidations: InvoiceRiskValidationResponse;
}

export interface EditInvoiceWithRiskValidationResponse extends InvoiceResponseV2 {
  isRiskValidationFailed: boolean;
  riskValidations: InvoiceRiskValidationResponse;
}

export interface EditInvoiceWithRiskValidationRequest {
  invoiceId: string;
  invoicePayload: InvoicePayloadV2;
  skipWarnings?: boolean;
}

export interface GetInvoiceCategoryItemListingsPayload {
  teamId: number;
  search: string;
  limit: number;
  page: number;
}

export interface IInvoiceListingCategory {
  id: number;
  name: string;
  lineItemsCount: number;
}

export interface IInvoiceListingItem {
  id: number;
  name: string;
  category: string;
  unitPrice: string;
  sku: string;
  description: string;
  taxes: AbstractITax[];
  unitCost?: string;
  markupPercentage?: string;
}

export interface GetInvoiceCategoryItemListingResponse {
  categories: GenericPaginationResponse<IInvoiceListingCategory>;
  lineItems: GenericPaginationResponse<IInvoiceListingItem>;
}

export interface InvoiceDownloadPayload {
  invoiceId: string;
}

export interface InvoiceTimelineEvent {
  title: string;
  subTitle: string;
  timestamp: string;
  doneBy: string;
  colourCode: string;
  webInfo: EventInfo;
  appInfo: EventInfo;
  reason: string;
}

export interface EventInfo {
  summary: boolean;
  cta: Cta;
}

export interface Cta {
  text: string;
  action: string;
  colour: string;
}

export interface InvoiceAttachmentList {
  attachmentResponse: AttachmentFile[];
  createdAt: string;
  createdBy: string;
}

export interface GetInvoiceAttachmentsResponse {
  invoiceAttachmentList: InvoiceAttachmentList;
}

// V2 API Interfaces (generic for both requests and responses)
export interface LineItemTaxV2 {
  id: number;
  additionalTaxId?: number;
  catalogueTaxId: number;
  name: string;
  percentage: number;
  amount: number;
}

export interface LineItemV2 {
  catalogueLineItemId?: number;
  name: string;
  qty: number;
  amount: number;
  unitPrice: number;
  unitCost?: number;
  markupPercentage?: number;
  additionalDetails: string;
  taxes?: LineItemTaxV2[];
  sku?: string;
}

export interface ServiceChargeTaxV2 {
  id: number;
  catalogueTaxId: number;
  name: string;
  percentage: number;
  amount: number;
}

export interface ServiceChargeV2 {
  name: string;
  amount: number;
  percentage: number;
  totalAmount: number;
  taxes?: ServiceChargeTaxV2[];
}

export interface IUniqueTaxRequestV2 {
  catalogueTaxId: number;
  name: string;
  percentage: number;
  amount: number;
}

export interface ServiceFeeV2 {
  enabled: boolean;
  type: string;
  value: number;
  basePoints: number;
  amount: number;
}

export interface TipV2 {
  enabled: boolean;
  type?: string;
}

export interface DiscountV2 {
  enabled: boolean;
  type: string;
  value: number;
  amount: number;
}

export interface InvoicePayloadV2 {
  customerId?: string;
  customer?: {
    name: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    companyName: string;
    referenceNumber: string;
    note: string;
    serviceAddress: AddressField[];
    billingAddress: AddressField | null;
    isBillingSameAsService: boolean;
  };
  customerServiceAddressId?: number;
  invoiceDate: string;
  invoiceDueDate: string;
  lineItems: LineItemV2[];
  lineItemAmount: number;
  discount: DiscountV2;
  subTotalAmount: number;
  serviceFee: ServiceFeeV2;
  serviceCharges: ServiceChargeV2[];
  serviceChargeAmount: number;
  taxes: IUniqueTaxRequestV2[];
  totalAmount: number;
  note: string;
  attachments?: IInvoiceAttachmentRequest[];
  paymentMethodList: PaymentModeInvoicePayload[];
  tip: TipV2;
  partialPaymentEnabled: boolean;
  reminderEnabled: boolean;
  reminders: InvoiceReminder[];
  signatureEnabled: boolean;
  tncId: number;
  referenceId?: string;
  storeName: string;
  version: string;
  source: InvoiceSourceEnum;
  relativeEntity?: {
    entityType: string;
    entityId: string;
    entityNumber: string;
  };
  sendEmailToCustomer?: boolean;
  autoFillKey?: string;
  isAutofillModified?: boolean;
}

// V2 API Response Interfaces
export interface MerchantV2 {
  id: string;
  name: string;
  taxId: string;
  address: string;
  phoneNumber: string;
  email: string;
  website: string;
  profilePicture: string;
}

// V2 Service Charge Response interface (different from request)
export interface ServiceChargeV2Response {
  id: number;
  name: string;
  amount: number;
  totalAmount: number;
  percentage: number;
  taxes: ServiceChargeTaxV2[];
}

export interface TaxV2Response {
  id: number;
  additionalTaxID: number;
  name: string;
  percentage: number;
  amount: number;
  catalogueTaxId: number;
}

export interface TransactionV2 {
  type: TransactionType;
  transactionId: string;
  transactionStatus: string;
  transactionCreatedAt: string;
  transactionCreatedBy: string;
  transactionDate: string;
  paymentMethod: string;
  paymentMethodName: string;
  paymentMethodIcon: string;
  invoicedAmount: number;
  paidAmount: number;
  tipAmount: number;
  tipPercentage: number;
  isManualKeyed: boolean;
  chargedCardOnFile: boolean;
  isManualPayment: boolean;
  cardDetails: {
    cardLast4: string;
    funding: string;
    network: string;
    networkIcon: string;
    paymentMethodSuffix: string;
    cardLast4Display: string;
  };
  isRefundExpired: boolean;
  refundTransactionId: string;
  refundStatus: string;
  refundDate: string;
  refundReason: string;
  refundAmount: number;
  refundAt: string;
  refundParentTransactionID: string;
  disputeTransactionId: string;
  disputeStatus: string;
  disputeAt: string;
  disputeDate: string;
  disputeReason: string;
  disputeAmount: number;
  disputeId: string;
  disputeParentTransactionID: string;
  returnTransactionId: string;
  returnStatus: string;
  returnAt: string;
  returnDate: string;
  returnReason: string;
  returnAmount: number;
  returnParentTransactionID: string;
  settlementAmount: number;
  settlementAt: string;
  settlementDate: string;
  settlementReason: string;
  settlementNote: string;
  payoutStatus: string;
  payoutCreatedAt: string;
  hasPermission: boolean;
}

export interface TeamInfoV2 {
  id: number;
  name: string;
  phone: string;
  email: string;
  website: string;
  address: string;
  profilePicture: string;
}

export interface InvoiceTimeline {
  textA: string;
  textB: string;
  textC: string;
  eventTag: string;
  colourCode: string;
  isLatest: boolean;
  appCtaList?: {
    text: string;
    deepLink: string;
    textColour: string;
    backgroundColour: string;
    borderColour: string;
  }[];
  webCtaList?: {
    text: string;
    action: string;
    textColour: string;
    backgroundColour: string;
    borderColour: string;
  }[];
}

export interface InvoiceByIdV2 {
  invoiceId: string;
  invoiceNumber: string;
  isPaid: boolean;
  merchantInfo: MerchantV2;
  invoiceCustomer: Customer;
  status: string;
  statusIndicator: string;
  note: string;
  attachments: Attachment[];
  createdDate: string;
  createdTime: string;
  paidDate: string;
  paidTime: string;
  paidDateTime: string;
  createdAt: string;
  paidAt: string;
  createdBy: string;
  invoiceDate: string;
  invoiceDueDate: string;
  currency: string;
  lineItems: LineItemV2[];
  totalLineItemAmount: number;
  discount: DiscountV2;
  discountAmount: number;
  subTotalAmount: number; //billable amount- amount before taxes and service fees
  serviceFeeAmount: number;
  serviceFee: ServiceFeeV2;
  serviceChargeAmount: number;
  serviceCharges: ServiceChargeV2Response[];
  totalTaxAmount: number;
  taxes: TaxV2Response[];
  totalAmount: number; // payable amount-final amount
  pendingAmount?: number;
  totalPaidAmount?: number;
  tip: TipV2;
  tipAmount?: number;
  tipPercentage?: number;
  partialPaymentEnabled: boolean;
  reminderEnabled: boolean;
  reminders: InvoiceReminder[];
  signatureEnabled: boolean;
  isInvoiceSigned: boolean;
  signatureCustomerName: string;
  signaturePresignedUrl: string;
  signedAt: string;
  paymentMethodList: PaymentMode[];
  paymentEnabled: boolean;
  transactions: TransactionV2[];
  transactionCount: number;
  qrId: string;
  checkoutURL: string; //checkout or payment URL
  invoiceLink: string; // invoice sharing, receipt viewing
  receiptQR: string;
  storeName: string;
  referenceId: string;
  version: string;
  invoiceMessage: string;
  tnc: TNC;
  isEditable: boolean;
  isModifiable: boolean;
  teamInfo: TeamInfoV2;
  timeline: InvoiceTimeline[];
  timelineStatus: string;
  relatedJobs: JobData[] | null;
  relatedEstimates: RelatedEstimate[] | null;
  trackingId: string;
  totalPaidAfterRefund: number;
  totalRefundedAmount: number;
  hasRefundableTransaction: boolean;
  totalInvoicedAmount: number;
  closureAmount: number;
  isClosed: boolean;
  closureReason: string;
  closureNote: string;
  hasManualPayment: boolean;
}

export interface InvoiceByIdResponseV2 {
  invoice: InvoiceByIdV2;
  customer: Customer;
}

export interface ShareInvoicePayload {
  teamId: string;
  invoiceId: string;
  type: ForwardFileType;
  shareVia: string;
  phone: string;
  email: string;
  updateCustomer: boolean;
  requestedAmount?: number;
}

export interface ShareInvoiceResponse {
  message: string;
  shareTrackingId: string;
}

export interface CreateCustomAmountTrackingPayload {
  invoiceId: string;
  requestedAmount: string;
  teamId: string;
}

export interface CreateCustomAmountTrackingResponse {
  trackingId: string;
  invoiceLink: string;
}

export interface GetInvoiceQRImagePayload {
  teamId: string;
  invoiceId: string;
  requestedAmount?: number;
}

export interface GetInvoiceQRImageResponse {
  qrImage: string;
}

export interface GetInvoicesPayload {
  teamId: number;
  merchantId: string[];
  customerId: string[];
  status: InvoiceStatus[];
  // TODO change to enum while integrating filters
  paymentMethod: string[];
  tipAmountNE: number;
  start: string;
  end: string;
  search: string;
  type: FileTypes;
  limit: number;
  page: number;
  selectedIds: string[];
}

export interface GetInvoiceSearchSuggestionResponse {
  suggestion: string[];
}

export interface GetInvoiceSearchSuggestionPayload {
  teamId: number;
}

export interface GetInvoicesV2Payload {
  teamId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  focusedColumns?: string[];
  customerId?: string[];
  createdAtFrom?: string;
  createdAtTo?: string;
  createdBy?: string[];
  dueDateFrom?: string;
  dueDateTo?: string;
  totalAmount?: string;
  refundStatus?: string;
  disputeStatus?: string;
  reminderStatus?: string;
  jobStatus?: string;
  estimateStatus?: string;
  status?: string[];
  timezone?: string;
  source?: string;
}

export interface InvoiceV2ColumnInfo {
  columnKey: string;
  columnLabel: string;
  priority: number;
  isEnabled: boolean;
  showAlways: boolean;
  isFocused: boolean;
}

export interface InvoiceV2ListItem {
  invoiceId: string;
  invoiceNumber: string;
  createdAt: string;
  status: string;
  tipAmount: string;
  payableAmount: string;
  pendingAmount: string;
  dueDate: string;
  merchantId: string;
  merchantProfilePicureUrl: string;
  merchantName: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  jobId: string;
  jobStatus: string;
  jobNumber: string;
  estimateId: string;
  estimateStatus: string;
  estimateNumber: number;
  totalPaidAmount: string;
  totalTipCollected: string;
  totalRefundedAmount: string;
  totalDisputedAmount: string;
  refundStatus: string;
  disputeStatus: string;
  paymentMethodList: string[];
  remindersCount: number;
  remindersSentCount: number;
  processingPaymentsCount: number;
  successfulPaymentsCount: number;
  lastSuccessfulRefundAt: string;
  lastSuccessfulTransactionAt: string;
  closureAmount: number;
  isClosed: boolean;
  closureReason: string;
  closureNote: string;
  closedAt: string;
}
export interface StatusBasedCount {
  allInvoicesCount: string;
  openInvoicesCount: string;
  pastDueInvoicesCount: string;
  paidInvoicesCount: string;
}

export interface GetInvoicesV2Response {
  invoices: InvoiceV2ListItem[];
  columnInfo: InvoiceV2ColumnInfo[];
  filteredCount: number;
  statusBasedCount: StatusBasedCount;
}

export interface ShareInvoicePayload {
  teamId: string;
  invoiceId: string;
  type: ForwardFileType;
  shareVia: string;
  phone: string;
  email: string;
  updateCustomer: boolean;
}

export interface MarkPaidPayload {
  teamId: string;
  invoiceId: string;
  paymentMethod: string;
  note: string;
  paymentAmount?: number;
  tipAmount?: number;
}

export interface MarkPaidResponse {
  invoiceId: string;
  teamId: number;
  transactionId: string;
  receiptMessage: string;
}

export interface DeleteInvoicePayload {
  teamId: number;
  invoiceId: string;
}

export interface DownloadInvoicePayload {
  invoiceId: string;
}

export type FilterOptionItem = { id?: string | number; value?: string; label?: string; [key: string]: unknown };

export interface GetInvoiceRefundableTransactionsPayload {
  teamId: string;
  invoiceId: string;
}

export type GetInvoiceRefundableTransactionsResponse = TransactionV2[];

export interface ParseInvoicePayload {
  content: string;
  timeZone: string;
  attachments: Array<{
    fileKey: string;
    fileType: string;
  }>;
  source: string;
}

export interface ParseInvoiceResponse {
  invoice: InvoiceByIdV2;
  customer: Customer;
  autofillKey: string;
  isAnyCustomerData?: boolean;
}

export interface AutoFillFeedbackPayload {
  autofillKey: string;
  isMerchantApproved: boolean;
  merchantRejectedReason?: string;
  merchantRejectedComment?: string;
  entityType: string;
}

export interface AutoFillFeedbackResponse {
  success: boolean;
  message: string;
}
